import { useCallback, useEffect, useState } from 'react';
import { useLazyGetPendingSignupsQuery } from '@api/pendingSignups';
import type { PendingSignupItem, PendingSignupStatus } from '@api/pendingSignups';

export type PendingSignupTableRow = {
  id: string;
  email: string;
  username: string;
  emailDomain: string;
  status: string;
  joiningDate: string;
  intendedCompanyName: string;
  matchedCompanyName: string;
  matchedClientCode: string;
  feedbackSubmitted: boolean;
};

const mapPendingSignupItem = (item: PendingSignupItem): PendingSignupTableRow => ({
  id: item.pendingSignupId,
  email: item.email,
  username: item.username || '',
  emailDomain: item.emailDomain || '',
  status: item.status,
  joiningDate: item.joiningDate || '',
  intendedCompanyName: item.intendedCompanyName || '',
  matchedCompanyName: item.matchedCompany?.companyName || '',
  matchedClientCode: item.matchedCompany?.clientCode || '',
  feedbackSubmitted: item.createCompanyRequestSubmitted,
});

type UsePendingSignupsTableParams = {
  pageNumber: number;
  pageSize: number;
  searchQuery: string;
  onPageReset?: () => void;
};

export const usePendingSignupsTable = ({
  pageNumber,
  pageSize,
  searchQuery,
  onPageReset,
}: UsePendingSignupsTableParams) => {
  const [rows, setRows] = useState<PendingSignupTableRow[]>([]);
  const [total, setTotal] = useState(0);
  const [sort, setSort] = useState('joiningDate');
  const [sortOrder, setSortOrder] = useState<'ASC' | 'DESC'>('DESC');
  const [statusFilter, setStatusFilter] = useState<PendingSignupStatus | ''>('');
  const [matchedClientCodeFilter, setMatchedClientCodeFilter] = useState('');
  const [feedbackSubmittedFilter, setFeedbackSubmittedFilter] = useState<string>('');

  const [getPendingSignups, { isLoading, isFetching }] = useLazyGetPendingSignupsQuery();

  const fetchPendingSignups = useCallback(async () => {
    try {
      const response = await getPendingSignups({
        page: pageNumber,
        size: pageSize,
        search: searchQuery || undefined,
        status: statusFilter || undefined,
        matchedClientCode: matchedClientCodeFilter || undefined,
        createCompanyRequestSubmitted:
          feedbackSubmittedFilter === '' ? undefined : feedbackSubmittedFilter === 'true',
        sort,
        sortOrder,
      }).unwrap();

      const items = response?.data?.items ?? [];
      setRows(items.map(mapPendingSignupItem));
      setTotal(response?.data?.totalElements ?? 0);
    } catch (error) {
      console.error('Error while fetching pending signups:', error);
      setRows([]);
      setTotal(0);
    }
  }, [
    getPendingSignups,
    pageNumber,
    pageSize,
    searchQuery,
    sort,
    sortOrder,
    statusFilter,
    matchedClientCodeFilter,
    feedbackSubmittedFilter,
  ]);

  useEffect(() => {
    fetchPendingSignups();
  }, [fetchPendingSignups]);

  const handleSort = useCallback(
    (sortKey: string) => {
      if (sortKey === sort) {
        setSortOrder((prev) => (prev === 'ASC' ? 'DESC' : 'ASC'));
      } else {
        setSort(sortKey);
        setSortOrder('ASC');
      }
      onPageReset?.();
    },
    [sort, onPageReset],
  );

  const handleStatusFilterChange = useCallback(
    (filter: string) => {
      setStatusFilter(filter as PendingSignupStatus | '');
      onPageReset?.();
    },
    [onPageReset],
  );

  const handleMatchedClientCodeFilterChange = useCallback(
    (filter: string) => {
      setMatchedClientCodeFilter(filter);
      onPageReset?.();
    },
    [onPageReset],
  );

  const handleFeedbackSubmittedFilterChange = useCallback(
    (filter: string) => {
      setFeedbackSubmittedFilter(filter);
      onPageReset?.();
    },
    [onPageReset],
  );

  return {
    rows,
    isLoading: isLoading || isFetching,
    total,
    sort,
    sortOrder,
    handleSort,
    statusFilter,
    handleStatusFilterChange,
    matchedClientCodeFilter,
    handleMatchedClientCodeFilterChange,
    feedbackSubmittedFilter,
    handleFeedbackSubmittedFilterChange,
  };
};
